import { useMutation, useQuery } from '@apollo/client';
import { IconRemove } from '@newfront-insurance/core-ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  cn,
} from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { Download, Loader2, Pencil, Search } from 'lucide-react';
import pluralize from 'pluralize';
import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useSkillListState } from './use-skill-list-state';
import { isSkillCommandDisabledByAccount, normalizeSkillCommand } from '../../../../lib/skill-command-utils';
import { isCustomSkillCommand, type SkillCommand } from '../../../../lib/skill-constants';
import type { CustomSkillRecord } from '../../../../queries/chats';
import { GET_CUSTOM_SKILLS } from '../../../../queries/chats';
import { DELETE_CUSTOM_SKILL } from '../../../../queries/mutations';
import { useChatOverlayContextSafe } from '../../contexts/chat-overlay-context';
import { useSkillsSafe } from '../../contexts/skills-context';
import {
  type NovaAddSkillClickSource,
  getSkillCommandAnalyticsProperties,
  NOVA_ANALYTICS_EVENTS,
  trackNovaEvent,
} from '../../lib/analytics';
import { getErrorMessage } from '../../lib/error-message';
import { useCustomSkillDownload } from '../custom-skill-download';
import { CreateSkillView } from '../custom-skills-ui';
import {
  SkillCategorySection,
  SkillListChevron,
  SkillListExampleUsage,
  SkillListExpandedHint,
  SkillListExpandedSection,
  SkillListExpandedSubLabel,
  SkillListItem,
} from '../skill-list-item';

export type SkillListVariant = 'default' | 'compact';

interface SkillListProps {
  commands: SkillCommand[];
  searchQuery: string;
  isOpen: boolean;
  onSelect: (command: SkillCommand) => void;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
  variant?: SkillListVariant;
  className?: string;
  showKeyboardHints?: boolean;
  onAddSkill?: () => void;
  onEditSkill?: (skill: CustomSkillRecord) => void;
  addSkillClickSource?: NovaAddSkillClickSource;
  selectedIndex?: number;
  onSelectedIndexChange?: (index: number) => void;
}

const styles = {
  default: {
    header: 'bg-steel-50 px-3.5 py-2.5',
    headerText: 'text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase',
    footer: 'bg-steel-50 px-3.5 py-2.5',
    footerText: 'text-steel-400 text-sm',
    listMaxHeight: 'max-h-[400px]',
    emptyTitle: 'text-steel-500 text-sm font-semibold',
    emptyDescription: 'text-steel-400 text-sm',
    emptyIcon: 'text-steel-500',
  },
  compact: {
    header: 'bg-steel-50 px-3.5 py-2.5',
    headerText: 'text-steel-400 text-[12px] font-semibold tracking-[1.2px] uppercase',
    footer: 'bg-steel-100 px-3.5 py-2.5',
    footerText: 'text-steel-400 text-[12px]',
    listMaxHeight: '',
    emptyTitle: 'text-steel-500 text-[12px] font-semibold',
    emptyDescription: 'text-steel-400 text-[12px] leading-4',
    emptyIcon: 'text-steel-400',
  },
} as const;

function getGroupCountLabel(commands: SkillCommand[]) {
  return pluralize('skill', commands.length);
}

function InlineIconButton({
  label,
  disabled,
  onClick,
  testId,
  ariaBusy,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
  ariaBusy?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-busy={ariaBusy}
      title={label}
      className="inline-flex h-7 w-7 items-center justify-center p-1 text-[#546A83] transition-colors hover:text-[#262D46] disabled:cursor-not-allowed disabled:opacity-50"
    >
      {children}
    </button>
  );
}

export function SkillList({
  commands,
  searchQuery,
  isOpen,
  onSelect,
  onClose,
  inputRef,
  variant = 'default',
  className,
  showKeyboardHints = true,
  onAddSkill,
  onEditSkill,
  addSkillClickSource = 'skill_menu',
  selectedIndex: controlledSelectedIndex,
  onSelectedIndexChange,
}: SkillListProps) {
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();
  const notify = useNotify();
  const skillsContext = useSkillsSafe();
  const overlayContext = useChatOverlayContextSafe();
  const { downloadCustomSkill, isDownloadingCustomSkill } = useCustomSkillDownload();
  const hasAccountInContext = Boolean(overlayContext?.accountUuid ?? overlayContext?.selectedAccount?.uuid);
  const isCommandDisabledByAccount = useCallback(
    (command: SkillCommand) => isSkillCommandDisabledByAccount(command, hasAccountInContext),
    [hasAccountInContext],
  );
  const handleSelectCommand = useCallback(
    (command: SkillCommand) => {
      if (isCommandDisabledByAccount(command)) {
        return;
      }
      onSelect(command);
    },
    [isCommandDisabledByAccount, onSelect],
  );
  const {
    filteredCommands,
    groupedCommands,
    selectedIndex,
    expandedCommandUuid,
    listRef,
    toggleExpanded,
    registerItemRef,
  } = useSkillListState({
    commands,
    searchQuery,
    isOpen,
    onSelect: handleSelectCommand,
    onClose,
    inputRef,
    selectedIndex: controlledSelectedIndex,
    onSelectedIndexChange,
    isCommandDisabled: isCommandDisabledByAccount,
  });
  const [skillToDelete, setSkillToDelete] = useState<CustomSkillRecord | null>(null);
  const [isCreateViewOpen, setIsCreateViewOpen] = useState(false);
  const [skillToUpdate, setSkillToUpdate] = useState<CustomSkillRecord | null>(null);
  const [deleteUuid, setDeleteUuid] = useState<string | null>(null);
  const { data: customSkillsData, refetch: refetchCustomSkills } = useQuery(GET_CUSTOM_SKILLS, {
    variables: { filters: { enabled: [true, false] } },
    skip: !isOpen,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [deleteCustomSkill] = useMutation(DELETE_CUSTOM_SKILL);

  const customSkillsByCommand = useMemo(
    () =>
      new Map(
        (customSkillsData?.customSkills ?? []).map((skill) => [normalizeSkillCommand(skill.command), skill] as const),
      ),
    [customSkillsData?.customSkills],
  );

  const refreshSkills = useCallback(async () => {
    await Promise.all([skillsContext?.refetchSkills(), refetchCustomSkills({ filters: { enabled: [true, false] } })]);
  }, [refetchCustomSkills, skillsContext]);

  const resolveCustomSkill = useCallback(
    (command: SkillCommand) => {
      if (!isCustomSkillCommand(command)) {
        return null;
      }

      const byId = (customSkillsData?.customSkills ?? []).find((skill) => skill.uuid === command.skill?.id);
      if (byId) {
        return byId;
      }

      return customSkillsByCommand.get(normalizeSkillCommand(command.handle)) ?? null;
    },
    [customSkillsByCommand, customSkillsData?.customSkills],
  );

  const handleDelete = useCallback(async () => {
    if (!skillToDelete) {
      return;
    }

    setDeleteUuid(skillToDelete.uuid);
    try {
      await deleteCustomSkill({
        variables: {
          uuid: skillToDelete.uuid,
        },
      });
      await refreshSkills();
      notify.success('Custom skill deleted.');
      setSkillToDelete(null);
    } catch (deleteError) {
      notify.error(getErrorMessage(deleteError, 'Unable to delete custom skill.'));
    } finally {
      setDeleteUuid(null);
    }
  }, [deleteCustomSkill, notify, refreshSkills, skillToDelete]);

  const handleAddSkill = useCallback(() => {
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.ADD_SKILL_CLICKED, {
      chatUuid: chatOverlayContext?.chatUuid,
      source: addSkillClickSource,
    });

    if (onAddSkill) {
      onAddSkill();
      return;
    }

    setSkillToUpdate(null);
    setIsCreateViewOpen(true);
  }, [addSkillClickSource, analytics, chatOverlayContext?.chatUuid, onAddSkill]);

  const handleEditSkill = useCallback(
    (skill: CustomSkillRecord) => {
      if (onEditSkill) {
        onEditSkill(skill);
        return;
      }

      setIsCreateViewOpen(false);
      setSkillToUpdate(skill);
    },
    [onEditSkill],
  );

  if (isCreateViewOpen) {
    return (
      <div className={cn('flex min-h-0 flex-1 flex-col overflow-hidden bg-white', className)}>
        <CreateSkillView onBack={() => setIsCreateViewOpen(false)} onChatStarted={() => setIsCreateViewOpen(false)} />
      </div>
    );
  }

  if (skillToUpdate) {
    return (
      <div className={cn('flex min-h-0 flex-1 flex-col overflow-hidden bg-white', className)}>
        <CreateSkillView onBack={() => setSkillToUpdate(null)} skillToUpdate={skillToUpdate} />
      </div>
    );
  }

  if (!isOpen) {
    return null;
  }

  const s = styles[variant];
  let itemIndex = 0;

  if (filteredCommands.length === 0) {
    const trimmedQuery = searchQuery.replace(/^\//, '').trim();
    return (
      <div className={cn('flex w-full flex-col overflow-hidden', className)}>
        <div className={s.header}>
          <span className={s.headerText}>Skills</span>
        </div>
        <div className="flex flex-col items-center gap-2.5 bg-white px-3.5 py-4 text-center">
          <Search className={cn('size-6', s.emptyIcon)} />
          <div className="flex flex-col gap-1">
            <p className={s.emptyTitle}>{trimmedQuery ? `No skills match “${trimmedQuery}”` : 'No skills match'}</p>
            <p className={s.emptyDescription}>Try a different keyword</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex w-full flex-col overflow-hidden', className)}>
      <div className={cn(s.header, 'flex items-center justify-between')}>
        <span className={s.headerText}>Skills</span>
        <button
          type="button"
          data-testid="skill-list-add-skill-button"
          onClick={handleAddSkill}
          className="text-brand-400 text-sm font-semibold"
        >
          + Add new skill
        </button>
      </div>

      <div ref={listRef} className={cn('overflow-y-auto', variant === 'default' ? s.listMaxHeight : 'flex-1')}>
        {groupedCommands.map((group) => (
          <SkillCategorySection
            key={group.source}
            title={group.label}
            countLabel={`${group.commands.length} ${getGroupCountLabel(group.commands)}`}
            variant={variant}
            testId={`skill-source-group-${group.source}`}
          >
            {group.commands.map((command) => {
              const currentIndex = itemIndex;
              const isSelected = currentIndex === selectedIndex;
              const isExpanded = expandedCommandUuid === command.id;
              const customSkill = resolveCustomSkill(command);
              const isDeletePending = customSkill?.uuid === deleteUuid;
              const isDownloadPending = customSkill ? isDownloadingCustomSkill(customSkill.uuid) : false;
              const isDisabledByAccount = isCommandDisabledByAccount(command);
              const sharedOwnerEmail =
                command.skill?.sourceDetails?.type === 'SHARED'
                  ? command.skill.sourceDetails.ownerEmail?.trim()
                  : undefined;
              itemIndex += 1;

              return (
                <SkillListItem
                  key={command.id}
                  primaryActionRef={(el) => registerItemRef(currentIndex, el)}
                  title={variant === 'compact' ? undefined : command.title}
                  handle={`/${command.handle}`}
                  description={command.description}
                  variant={variant}
                  isExpanded={isExpanded}
                  isSelected={isSelected}
                  isDisabled={isDeletePending || isDownloadPending || isDisabledByAccount}
                  disabledTooltip={isDisabledByAccount ? 'Account selection required' : undefined}
                  onPrimaryAction={() => handleSelectCommand(command)}
                  primaryActionAriaLabel={undefined}
                  inlineActions={
                    customSkill ? (
                      <>
                        {customSkill.sourceFileUuid ? (
                          <InlineIconButton
                            label={`Download ${customSkill.frontmatter.name}`}
                            disabled={isDeletePending || isDownloadPending}
                            onClick={() => void downloadCustomSkill(customSkill)}
                            testId="skill-list-download-button"
                            ariaBusy={isDownloadPending}
                          >
                            {isDownloadPending ? (
                              <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin text-[#546A83]" />
                            ) : (
                              <Download aria-hidden="true" className="h-4 w-4 text-[#546A83]" />
                            )}
                          </InlineIconButton>
                        ) : null}

                        <InlineIconButton
                          label={`Delete ${customSkill.frontmatter.name}`}
                          disabled={isDeletePending || isDownloadPending}
                          onClick={() => setSkillToDelete(customSkill)}
                          testId="skill-list-delete-skill-button"
                        >
                          <span aria-hidden="true">
                            <IconRemove color="currentColor" size={16} />
                          </span>
                        </InlineIconButton>

                        <InlineIconButton
                          label={`Edit ${customSkill.frontmatter.name}`}
                          disabled={isDeletePending || isDownloadPending}
                          onClick={() => handleEditSkill(customSkill)}
                          testId="skill-list-edit-skill-button"
                        >
                          <Pencil aria-hidden="true" className="h-4 w-4" />
                        </InlineIconButton>
                      </>
                    ) : undefined
                  }
                  trailingAction={
                    <SkillListChevron
                      isExpanded={isExpanded}
                      onClick={() => {
                        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.SKILL_COMMAND_EXPANDED, {
                          ...getSkillCommandAnalyticsProperties(command),
                          chatUuid: chatOverlayContext?.chatUuid,
                          expanded: !isExpanded,
                        });
                        toggleExpanded(command.id, currentIndex);
                      }}
                      label={isExpanded ? `Collapse ${command.handle}` : `Expand ${command.handle}`}
                      disabled={isDeletePending || isDownloadPending}
                      variant={variant}
                    />
                  }
                  expandedContent={
                    <>
                      <SkillListExpandedSection label="What it does" variant={variant}>
                        <div className="flex min-w-0 flex-col gap-1">
                          <SkillListExpandedSubLabel variant={variant}>
                            Use `/{command.handle}` to load this skill
                          </SkillListExpandedSubLabel>
                          <div className="mt-2">
                            <SkillListExpandedHint variant={variant}>{command.overview}</SkillListExpandedHint>
                          </div>
                        </div>
                      </SkillListExpandedSection>

                      {command.exampleUsage ? (
                        <SkillListExpandedSection label="Example usage" variant={variant}>
                          <SkillListExampleUsage handle={command.handle} variant={variant}>
                            {command.exampleUsage}
                          </SkillListExampleUsage>
                        </SkillListExpandedSection>
                      ) : null}

                      {sharedOwnerEmail ? (
                        <SkillListExpandedSection label="Source" variant={variant}>
                          <SkillListExpandedHint variant={variant}>Shared by {sharedOwnerEmail}</SkillListExpandedHint>
                        </SkillListExpandedSection>
                      ) : null}
                    </>
                  }
                />
              );
            })}
          </SkillCategorySection>
        ))}
      </div>

      {showKeyboardHints ? (
        <div className={s.footer}>
          <span className={s.footerText}>
            <kbd className="rounded px-1 py-0.5">↑↓</kbd> to navigate{' '}
            <kbd className="ml-2 rounded px-1 py-0.5">Tab</kbd>/<kbd className="rounded px-1 py-0.5">Enter</kbd> to
            select <kbd className="ml-2 rounded px-1 py-0.5">Esc</kbd> to cancel
          </span>
        </div>
      ) : null}

      <AlertDialog open={Boolean(skillToDelete)} onOpenChange={(open) => !open && setSkillToDelete(null)}>
        <AlertDialogContent className="w-[349px]">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete custom skill</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            {skillToDelete ? (
              <>
                This removes <span className="font-mono font-semibold text-[#257FFB]">/{skillToDelete.command}</span>{' '}
                from your custom skills list.
              </>
            ) : (
              ''
            )}
          </AlertDialogDescription>
          <AlertDialogFooter className="flex-row justify-end">
            <AlertDialogCancel disabled={Boolean(deleteUuid)}>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="dangerous" disabled={Boolean(deleteUuid)} onClick={() => void handleDelete()}>
              {deleteUuid ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
